import { SensorsActionTypes } from '../types/sensors';
import { InConfigurationActionTypes } from '../types/in-configuration';
import { Filter, FilterOfValueType } from '../types/filters';
import { Sort } from '../types/sort';
import { JSONValue } from '../types/json';
import { SenseiApi } from '../utils/api';
import { CapturesActionTypes } from '../types/captures';
import { Organization } from '../types/api';
import { ALL_ORGANIZATIONS } from '../types/all-organizations';

const PAGE_SIZE = 50;

// A function to clean up some of the filter parsing logic in generateParams
// FIXME: ideally, the casting shouldn't have to exist in this function. Something is wrong with the
// `Fitler*` types.
function extractSingleFilterValue<T extends JSONValue>(
  filters: Array<Filter>,
  definitionId: string,
  operatorId: string,
): FilterOfValueType<T> | undefined {
  return filters.find(
    (filter) =>
      filter.definitionId === definitionId && filter.operatorId === operatorId,
  ) as FilterOfValueType<T> | undefined;
}

function extractMultipleFilterValues<T extends JSONValue>(
  filters: Array<Filter>,
  definitionId: string,
  operatorId: string,
): Array<FilterOfValueType<T>> {
  return filters.filter(
    (filter) =>
      filter.definitionId === definitionId && filter.operatorId === operatorId,
  ) as Array<FilterOfValueType<T>>;
}

export function generateParams(
  filters: Array<Filter>,
  searchText: string,
  sort: Sort,
  organizationId: Organization['id'] | null,
  sensorStateHumanReadableNameMapping: { [state: string]: string },
): { [key: string]: string | number | boolean } {
  // Apply filters to the results that are returned.
  const accuracyRangeLessThan = extractSingleFilterValue<string>(
    filters,
    'total_accuracy',
    'lt',
  );
  const accuracyRangeGreaterThan = extractSingleFilterValue<string>(
    filters,
    'total_accuracy',
    'gt',
  );

  const inMPI = extractSingleFilterValue<boolean>(filters, 'in_mpi', 'equals');

  const currentStates = extractMultipleFilterValues<Array<string>>(
    filters,
    'current_state',
    'in',
  );

  const assignedTeam = extractSingleFilterValue<Array<string>>(
    filters,
    'assigned_team',
    'equals',
  );

  const humanReadableStateNames = extractMultipleFilterValues<Array<string>>(
    filters,
    'human_readable_state_name',
    'in',
  );

  const requiresHumanInteraction = filters.find(
    (i) =>
      (i.definitionId === 'human_readable_state_name' ||
        i.definitionId === 'current_state') &&
      i.operatorId === 'requires_human_interaction',
  ) as FilterOfValueType<boolean> | undefined;

  const statusFilters = extractMultipleFilterValues<Array<string>>(
    filters,
    'statuses',
    'in',
  );

  const serialNumberEqualsFilter = extractSingleFilterValue<string>(
    filters,
    'serial_number',
    'equals',
  );

  const serialNumberStartsWithFilter = extractSingleFilterValue<string>(
    filters,
    'serial_number',
    'startswith',
  );

  const sensorKindFilter = extractMultipleFilterValues<Array<string>>(
    filters,
    'serial_number',
    'kind',
  );

  const sensorHardwareModelFilter = extractMultipleFilterValues<Array<string>>(
    filters,
    'serial_number',
    'hardware_model',
  );

  const sensorIsVirtualFilter = extractSingleFilterValue<string>(
    filters,
    'serial_number',
    'is_virtual',
  );

  const firstOnlineMin = extractSingleFilterValue<string>(
    filters,
    'first_online',
    'gt',
  );
  const firstOnlineMax = extractSingleFilterValue<string>(
    filters,
    'first_online',
    'lt',
  );

  const qaedMin = extractSingleFilterValue<string>(
    filters,
    'qaed_events',
    'gte',
  );
  const qaedMax = extractSingleFilterValue<string>(
    filters,
    'qaed_events',
    'lte',
  );

  const osVersion = extractSingleFilterValue<string>(
    filters,
    'os_version',
    'equals',
  );

  const annotatedEventsMin = extractSingleFilterValue<string>(
    filters,
    'annotated_events',
    'gte',
  );
  const annotatedEventsMax = extractSingleFilterValue<string>(
    filters,
    'annotated_events',
    'lte',
  );

  let currentStateList = [];
  if (currentStates.length > 0) {
    currentStateList = [
      ...currentStateList,
      ...currentStates.flatMap((s) => s.value),
    ];
  }
  if (humanReadableStateNames.length > 0) {
    const humanReadableNameToStates = {};
    for (const [k, v] of Object.entries(sensorStateHumanReadableNameMapping)) {
      if (!humanReadableNameToStates[v]) {
        humanReadableNameToStates[v] = [];
      }
      humanReadableNameToStates[v].push(k);
    }
    currentStateList = [
      ...currentStateList,
      ...humanReadableStateNames.flatMap((filter) =>
        filter.value.flatMap((val) => humanReadableNameToStates[val]),
      ),
    ];
  }

  const currentStateQueryParamValue =
    currentStateList.length > 0 ? currentStateList.join(',') : undefined;

  const params = {
    page_size: PAGE_SIZE,
    organization_ids: organizationId === null ? undefined : organizationId,
    accuracy_less_than: accuracyRangeLessThan
      ? accuracyRangeLessThan.value
      : undefined,
    accuracy_greater_than: accuracyRangeGreaterThan
      ? accuracyRangeGreaterThan.value
      : undefined,
    in_mpi: inMPI !== undefined ? `${inMPI.value}` : undefined,
    current_states: currentStateQueryParamValue,
    requires_human_interaction:
      requiresHumanInteraction !== undefined
        ? requiresHumanInteraction.value
        : undefined,
    assigned_teams:
      assignedTeam !== undefined ? assignedTeam.value.join(',') : undefined,
    statuses:
      statusFilters.length > 0
        ? statusFilters.flatMap((i) => i.value).join(',')
        : undefined,
    serial_number:
      serialNumberEqualsFilter !== undefined
        ? serialNumberEqualsFilter.value
        : undefined,
    kind:
      sensorKindFilter.length > 0
        ? sensorKindFilter.flatMap((i) => i.value).join(',')
        : undefined,
    hardware_model:
      sensorHardwareModelFilter.length > 0
        ? sensorHardwareModelFilter.flatMap((i) => i.value).join(',')
        : undefined,
    is_virtual: sensorIsVirtualFilter ? sensorIsVirtualFilter.value : undefined,
    serial_number_prefix:
      serialNumberStartsWithFilter !== undefined
        ? serialNumberStartsWithFilter.value
        : undefined,
    first_heartbeat_after:
      firstOnlineMin !== undefined ? `${firstOnlineMin.value}` : undefined,
    first_heartbeat_before:
      firstOnlineMax !== undefined ? `${firstOnlineMax.value}` : undefined,
    qaed_event_count_min:
      qaedMin !== undefined ? `${qaedMin.value}` : undefined,
    qaed_event_count_max:
      qaedMax !== undefined ? `${qaedMax.value}` : undefined,
    os_version: osVersion !== undefined ? `${osVersion.value}` : undefined,
    total_annotated_min:
      annotatedEventsMin !== undefined
        ? `${annotatedEventsMin.value}`
        : undefined,
    total_annotated_max:
      annotatedEventsMax !== undefined
        ? `${annotatedEventsMax.value}`
        : undefined,
  };

  if (searchText.length > 0) {
    params['search'] = searchText;
  }

  if (sort.column !== null && sort.direction !== 'none') {
    params['ordering'] = `${sort.direction === 'desc' ? '' : '-'}${
      sort.column
    }`;
    params['sort_direction'] = sort.direction;
  }
  return params;
}

// Fetch a list of all sensors and put them into the `SensorsStore`
export async function fetchInitialSensors(
  dispatch,
  jwtToken: string,
  filters: Filter[],
  sort: Sort,
  searchText: string,
  organizationId: Organization['id'] | null,
) {
  dispatch({
    type: InConfigurationActionTypes.IN_CONFIGURATION_LOAD_START,
  });

  const sensorStateList = await SenseiApi.listSensorStates(jwtToken);

  // Apply filters to the results that are returned.
  const params = generateParams(
    filters,
    searchText,
    sort,
    organizationId,
    sensorStateList.human_readable_state_names,
  );

  let organizations: any, sensors: any;
  try {
    [organizations, sensors] = await Promise.all([
      SenseiApi.organizationList(jwtToken),
      SenseiApi.sensorList(jwtToken, params),
    ]);
  } catch (error) {
    dispatch({
      type: InConfigurationActionTypes.IN_CONFIGURATION_LOAD_ERROR,
      error,
    });
    return;
  }

  dispatch({
    type: InConfigurationActionTypes.IN_CONFIGURATION_LOAD_COMPLETE,
    organizations,
    sensors: sensors.results,
    total: sensors.total,
    filters,
    sort,
    searchText,
    sensorStateChoices: sensorStateList.results
      .sort()
      .map((sensorState) => ({ id: sensorState, label: sensorState })),
    sensorStateHumanReadableNameMapping:
      sensorStateList.human_readable_state_names,
  });
}

// Fetch a list of all sensors and put them into the `SensorsStore`
export async function fetchNextPageOfSensors(
  dispatch,
  jwtToken: string,
  filters: Filter[],
  sort: Sort,
  searchText: string,
  page: number,
  organizationId: Organization['id'] | null,
  sensorStateHumanReadableNameMapping: { [state: string]: string },
) {
  dispatch({
    type: InConfigurationActionTypes.IN_CONFIGURATION_NEXT_PAGE_LOAD_LOADING,
  });

  // Apply filters to the results that are returned.
  const params = {
    ...generateParams(
      filters,
      searchText,
      sort,
      organizationId,
      sensorStateHumanReadableNameMapping,
    ),
    page: page,
  };

  let sensors: any;
  try {
    sensors = await SenseiApi.sensorList(jwtToken, params);
  } catch (error) {
    dispatch({
      type: InConfigurationActionTypes.IN_CONFIGURATION_NEXT_PAGE_LOAD_ERROR,
      error,
    });
    return;
  }

  dispatch({
    type: InConfigurationActionTypes.IN_CONFIGURATION_NEXT_PAGE_LOAD_COMPLETE,
    sensors: sensors.results,
  });
}
